import type {
  SiteCmsBlock,
  SiteCmsMenuItem,
  SiteCmsPageDraft,
  SiteCmsRichNode,
} from "../types/entities";

/** One link walker for editor dependency checks and publication resources. */
export function cmsBlockLinks(block: SiteCmsBlock): string[] {
  const result: string[] = [];
  if (block.type === "button" || block.type === "cover" || block.type === "cta")
    result.push(block.href);
  if (block.type === "partners")
    result.push(...block.items.map((item) => item.url));
  if (block.type === "text" || block.type === "imageText") {
    const visit = (node: SiteCmsRichNode): void => {
      for (const mark of node.marks ?? [])
        if (mark.type === "link" && mark.attrs) result.push(mark.attrs.href);
      for (const child of node.content ?? []) visit(child);
    };
    if (block.rich) visit(block.rich);
    else
      for (const match of block.text.matchAll(/\[[^\]]*\]\(([^\s)]+)\)/g))
        result.push(match[1]);
  }
  return result.filter(Boolean);
}
export function cmsLinkedPage(href: string): string | null {
  const clean = href.split(/[?#]/, 1)[0];
  if (clean === "/") return "home";
  const match = /^\/([a-z0-9-]+)\/?$/.exec(clean);
  return match?.[1] ?? null;
}
export function cmsLinkedDocument(href: string): string | null {
  return (
    /^\/documents\/([a-z0-9-]+)\.pdf$/.exec(href.split(/[?#]/, 1)[0])?.[1] ??
    null
  );
}
export type CmsPageReference = {
  label: string;
  pageSlug?: string;
  blockIndex?: number;
};
export function cmsPageReferences(
  pages: readonly SiteCmsPageDraft[],
  menu: readonly SiteCmsMenuItem[],
  target: string,
): CmsPageReference[] {
  const references: CmsPageReference[] = [];
  function visit(items: readonly SiteCmsMenuItem[]): void {
    for (const item of items) {
      if (item.type === "PAGE" && item.slug === target)
        references.push({ label: `Menu · ${item.label}` });
      visit(item.children);
    }
  }
  visit(menu);
  for (const page of pages) {
    if (page.slug === target) continue;
    page.blocks.forEach((block, blockIndex) => {
      if (cmsBlockLinks(block).some((href) => cmsLinkedPage(href) === target)) {
        references.push({
          label: `${page.title} · section ${blockIndex + 1}`,
          pageSlug: page.slug,
          blockIndex,
        });
      }
    });
  }
  return references;
}
